import { z } from "zod";

import { campaignSchema } from "./campaign/campaignSchema";
import { communitySchema } from "./community/communitySchema";
import { deeplinkSchema } from "./deeplinkSchema";
import { groupSchema } from "./groupSchema";
import { playablePrayerSchema } from "./prayer/playablePrayerSchema";
import { userSchema } from "./user/userSchema";

export enum StreakPart {
  Start = 0,
  Middle = 1,
  End = 2,
}

const streakSchema = z.object({
  current_total: z.number(),
  activity: z.array(
    z.object({
      has_session: z.boolean(),
      is_today: z.boolean(),
      goal_met: z.boolean(),
      date: z.string().datetime(),
      streak: z.nativeEnum(StreakPart).optional().nullable(),
    }),
  ),
});

const summarySchema = z.object({
  type: z.literal("summary"),
  id: z.number(),
  streak: streakSchema,
  sessions: playablePrayerSchema.array(),
  groups: groupSchema.array(),
});

const prayerGoalSchema = z.object({
  type: z.literal("prayer_goal_met"),
  id: z.number(),
  completion_message: z.string(),
  title: z.string(),
  prayer_goal: z.object({
    id: z.number(),
    name: z.string(),
    desc: z.string(),
    complete_image_url: z.string().url(),
    incomplete_image_url: z.string().url(),
    is_met: z.boolean(),
    current_item_count: z.number(),
    total_items_needed: z.number(),
    progress_desc: z.string(),
  }),
});

const highlightSchema = z.object({
  type: z.literal("highlights"),
  id: z.number(),
  sessions: playablePrayerSchema.array(),
  groups: groupSchema.array(),
});

const reflectionSchema = z.object({
  type: z.literal("reflection"),
  id: z.number(),
  session: playablePrayerSchema,
  groups: groupSchema.array(),
});

const communityPromptSchema = z.object({
  type: z.literal("community_prompt"),
  id: z.number(),
  session: playablePrayerSchema,
  community: communitySchema,
  response: z.object({
    id: z.number(),
    text: z.string(),
    user: userSchema,
  }),
});

const forYouSchema = z.object({
  type: z.literal("for_you"),
  id: z.number(),
  title: z.string(),
  recommendations: deeplinkSchema.array(),
});

const promptSchema = z.object({
  type: z.literal("prompt"),
  id: z.number(),
  session: playablePrayerSchema,
  parish: z.any(), // TODO
  response: z.any(), // TODO
});

const ratingSchema = z.object({
  type: z.literal("rating"),
  id: z.number(),
});

const subscriptionSchema = z.object({
  type: z.literal("subscription"),
  id: z.number(),
});

/** if we ever see this something weird has happened */
const signupSchema = z.object({
  type: z.literal("signup"),
  id: z.number(),
});

const intentionsSchema = z.object({
  type: z.literal("intentions"),
  id: z.number(),
  intention: z.object({
    id: z.number(),
    author_text: z.string().nullable(),
    text: z.string(),
    is_pinned: z.boolean(),
    group_id: z.number().nullable(),
    post_id: z.number().nullable(),
    is_private: z.boolean(),
    created_at: z.string().datetime(),
  }),
});

const streakTypeSchema = z.object({
  type: z.literal("streak"),
  id: z.number(),
  title: z.string(),
  subtitle: z.string(),
  streak: streakSchema,
});

const downloadSchema = z.object({
  type: z.literal("download"),
  id: z.number(),
});

const givingCampaignSchema = z.object({
  type: z.literal("giving_campaign"),
  id: z.number(),
  campaign: campaignSchema,
});

const giftHallowSchema = z.object({
  type: z.literal("gift_hallow"),
  id: z.number(),
  title: z.string().optional().nullable(),
  desc: z.string().optional().nullable(),
  button_text: z.string().optional().nullable(),
  deeplink: z.string().optional().nullable(),
});

const defaultSchema = z.object({
  type: z.literal("default"),
  id: z.number(),
  super_title: z.string().optional().nullable(),
  image_url: z.string().url().optional().nullable(),
  title: z.string().optional().nullable(),
  desc: z.string().optional().nullable(),
  button_text: z.string().optional().nullable(),
  deeplink: z.string().optional().nullable(),
  secondary_button_text: z.string().optional().nullable(),
  secondary_deeplink: z.string().optional().nullable(),
});

const postPrayerStepSchema = z.discriminatedUnion("type", [
  summarySchema,
  prayerGoalSchema,
  highlightSchema,
  reflectionSchema,
  communityPromptSchema,
  forYouSchema,
  promptSchema,
  ratingSchema,
  subscriptionSchema,
  signupSchema,
  intentionsSchema,
  streakTypeSchema,
  downloadSchema,
  givingCampaignSchema,
  giftHallowSchema,
  defaultSchema,
]);

export const postPrayerSchema = z.object({
  ask_for_rating: z.boolean(),
  collection_id: z.number().nullable(),
  image_url: z.string().url(),
  sessions: playablePrayerSchema.array(),
  groups: groupSchema.array(),
  steps: postPrayerStepSchema.array(),
});

export type PostPrayerResponse = z.infer<typeof postPrayerSchema>;
export type SummaryPostPrayer = z.infer<typeof summarySchema>;
export type StreakPostPrayer = z.infer<typeof streakTypeSchema>;
export type PrayerGoalPostPrayer = z.infer<typeof prayerGoalSchema>;
export type DefaultPostPrayer = z.infer<typeof defaultSchema>;
export type IntentionsPostPrayer = z.infer<typeof intentionsSchema>;
export type SignupPostPrayer = z.infer<typeof signupSchema>;
export type SubscriptionPostPrayer = z.infer<typeof subscriptionSchema>;
export type RatingPostPrayer = z.infer<typeof ratingSchema>;
export type PromptPostPrayer = z.infer<typeof promptSchema>;
export type ForYouPostPrayer = z.infer<typeof forYouSchema>;
export type CommunityPromptPostPrayer = z.infer<typeof communityPromptSchema>;
export type ReflectionPostPrayer = z.infer<typeof reflectionSchema>;
export type HighlightsPostPrayer = z.infer<typeof highlightSchema>;
export type DownloadPostPrayer = z.infer<typeof downloadSchema>;
export type GivingCampaignStep = z.infer<typeof givingCampaignSchema>;
export type GiftHallowStep = z.infer<typeof giftHallowSchema>;
export type PostPrayerStep = z.infer<typeof postPrayerStepSchema>;
